import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const today = new Date();
  const lastUpdated = today.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/privacy-policy" />
        <title>Privacy Policy | Extreme Clean Australia</title>
      </Helmet>
      <div className="min-h-screen bg-white pt-32 md:pt-52 pb-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <h1 className="font-heading font-bold text-4xl md:text-5xl leading-snug text-[#0A0A0A] mb-4">
          Privacy Policy
        </h1>
        <p className="text-gray-500 mb-12">Last updated: {lastUpdated}</p>

        <div className="prose prose-lg max-w-none text-gray-700">
          <p className="mb-8">
            Extreme Pty Ltd (ACN 663 841 361, ABN 45 760 540 704), trading as Extreme Clean Australia ("ECA", "we", "us", "our"), is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store and protect personal information when you use our website or engage our commercial and industrial cleaning services.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">1. Who We Are</h2>
          <p className="mb-6">
            Extreme Clean Australia is a Western Australian commercial and industrial cleaning company, registered address 18A/92–94 Poole Street, Welshpool WA 6106. For any privacy-related questions or requests, contact us at{' '}
            <a href="mailto:info.eca@extreme-clean.com.au" className="text-[#3B82F6] hover:underline">info.eca@extreme-clean.com.au</a> or{' '}
            <a href="tel:0861875523" className="text-[#3B82F6] hover:underline">(08) 6187 5523</a>.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">2. Information We Collect</h2>
          <p className="mb-4">
            We collect personal information you provide directly to us, including through our website's contact and quote request forms. This may include:
          </p>
          <ul className="list-disc pl-6 mb-6 space-y-2">
            <li>Full name</li>
            <li>Email address</li>
            <li>Contact number</li>
            <li>Property or site address</li>
            <li>Details of the service you are enquiring about</li>
          </ul>
          <p className="mb-6">
            We do not collect sensitive information (such as health or financial details) through our website.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">3. How We Use Your Information</h2>
          <p className="mb-6">
            We use the information you provide to respond to enquiries and quote requests, schedule and deliver cleaning services, maintain accurate customer and job records, and communicate with you about existing or upcoming work. We do not use your information for unrelated marketing purposes without your consent.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">4. Where Your Information Is Stored</h2>
          <p className="mb-6">
            Customer and job information is stored in Jobber, a third-party field service management platform we use to manage bookings, scheduling and customer records. Jobber maintains its own security and privacy practices, and we only share the information necessary to deliver our services to you.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">5. Website Analytics</h2>
          <p className="mb-6">
            Our website uses Google Analytics (GA4) to understand how visitors use our site, such as which pages are viewed and how visitors navigate the site. This data is aggregated and anonymised where possible and helps us improve the website. We do not currently use any advertising or retargeting tracking tools (such as social media pixels) on our website.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">6. Disclosure of Information</h2>
          <p className="mb-6">
            We do not sell or trade your personal information. We may disclose information to trusted service providers who help us operate our business (such as Jobber and Google Analytics), or where required by law.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">7. Data Security</h2>
          <p className="mb-6">
            We take reasonable steps to protect the personal information we hold from misuse, loss, and unauthorised access, use, modification or disclosure.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">8. Access and Correction</h2>
          <p className="mb-6">
            You may request access to, or correction or deletion of, the personal information we hold about you by contacting us at{' '}
            <a href="mailto:info.eca@extreme-clean.com.au" className="text-[#3B82F6] hover:underline">info.eca@extreme-clean.com.au</a>. We will respond to reasonable requests within a reasonable timeframe.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">9. Changes to This Policy</h2>
          <p className="mb-6">
            We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated revision date.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">10. Contact Us</h2>
          <div className="bg-gray-50 p-6 rounded-lg">
            <p className="font-semibold text-[#0A0A0A] mb-2">Extreme Pty Ltd (ACN 663 841 361)</p>
            <p className="mb-1">Trading as Extreme Clean Australia</p>
            <p className="mb-1">ABN 45 760 540 704</p>
            <p className="mb-1">18A/92–94 Poole Street, Welshpool WA 6106</p>
            <p className="mb-1">Phone: <a href="tel:0861875523" className="text-[#3B82F6] hover:underline">(08) 6187 5523</a></p>
            <p>Email: <a href="mailto:info.eca@extreme-clean.com.au" className="text-[#3B82F6] hover:underline">info.eca@extreme-clean.com.au</a></p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PrivacyPolicy;
